import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { ConversationEnvironment } from '@source/mails/entities/mail.js';
import { endOfDay, format, startOfDay } from 'date-fns';
import { html, nothing } from 'lit';
import { guard } from 'lit/directives/guard.js';
import { OdooEntity } from '../../entities/odoo.js';
import { OdooTicketDetailView } from './OdooTicketDetailView.js';

export const OdooTicketDashboard = stateful(() => {
  // Use useState for the selected conversation and filters
  const [environmentFilter, setEnvironmentFilter] = useState<ConversationEnvironment | 'all'>('all');
  const [selectedConversationID, setSelectedConversationID] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [odooModelsFilter, setOdooModelsFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [integrationsTest, setIntegrationstest] = useState<'yes' | 'no'>('no');
  const [checkedByHuman, setCheckedByHuman] = useState<'all' | 'yes' | 'no'>('all');

  // Function to handle row click
  const handleRowClick = (conversation: OdooEntity) => {
    setSelectedConversationID(conversation._id);
  };

  // Function to close the detail view
  const closeDetailView = () => {
    setSelectedConversationID(null);
  };

  // Function to clear all filters
  const clearFilters = () => {
    setEnvironmentFilter('all');
    setSearchText('');
    setOdooModelsFilter('');
    setStartDate('');
    setEndDate('');
    setIntegrationstest('no');
    setCheckedByHuman('all');
  };

  const conversationDataviewOptions = useMemo(() => {
    const filterAspects: any[] = [];

    // Add date range filter - if both dates are provided, use range mode
    if (startDate && endDate) {
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'odooCreateDate',
          firstValue: startOfDay(new Date(startDate)),
          secondValue: endOfDay(new Date(endDate)),
          mode: 'range',
        }),
      );
    } else if (startDate) {
      // Only start date - use after mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'odooCreateDate',
          firstValue: startOfDay(new Date(startDate)),
          mode: 'after',
        }),
      );
    } else if (endDate) {
      // Only end date - use before mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'odooCreateDate',
          secondValue: endOfDay(new Date(endDate)),
          mode: 'before',
        }),
      );
    }
    if (integrationsTest === 'yes') {
      filterAspects.push(
        new BooleanAspect({
          fieldName: 'isHistoricalOrIntegrationTest',
          value: true,
        }),
      );
    }

    // Add checked by human filter
    if (checkedByHuman === 'yes') {
      filterAspects.push(
        new BooleanAspect({
          fieldName: 'isCheckedBySupport',
          value: true,
        }),
      );
    } else if (checkedByHuman === 'no') {
      // For "not checked", we want to include both false and null values
      // Use StringAspect with empty mode to catch all non-true values (null, false, undefined)
      filterAspects.push(
        new StringAspect({
          fieldName: 'isCheckedBySupport',
          mode: 'empty',
        }),
      );
    }

    return [
      buildFullOptions<typeof OdooEntity>({
        class: OdooEntity,
        customColumns: [
          {
            fieldName: 'odooTicketID',
            columnName: 'Odoo Ticket ID',
            columnWidth: '140px',
            renderItem: value => html` ${value.odooTicketID || 'N/A'} `,
          },
          { fieldName: 'subject', columnName: 'Subject', renderItem: value => html` ${value.subject || 'N/A'} ` },
          {
            fieldName: 'from',
            columnName: 'From',
            columnWidth: '200px',
            renderItem: value => html` ${value.from || 'N/A'} `,
          },
          {
            fieldName: 'odooTags',
            columnName: 'Odoo Tags',
            renderItem: value => html`
              ${value.odooTags?.filter(obj => !obj.includes(':') && !obj.includes('my_data')).join(', ') ||
              'Zuvor schon gesetzt'}
            `,
          },
          {
            fieldName: 'isCheckedBySupport',
            columnName: 'Checked by Support',
            columnWidth: '100px',
            renderItem: value =>
              value.isCheckedBySupport
                ? html`<span class="text-secondary">✅</span>`
                : html`<span class="text-error">❌</span>`,
          },
          {
            fieldName: 'isOdooActive',
            columnName: 'Active in Odoo',
            columnWidth: '100px',
            renderItem: value =>
              value.isOdooActive !== false
                ? html`<span class="text-secondary">✅</span>`
                : html`<span class="text-error">❌</span>`,
          },
          {
            fieldName: 'createdAt',
            columnName: 'Created At',
            columnWidth: '175px',
            renderItem: value => format(value.createdAt ?? new Date(), 'Pp'),
          },
          {
            fieldName: 'odooCreateDate',
            columnName: 'Odoo Create Date',
            columnWidth: '175px',
            renderItem: value => format(value.odooCreateDate ?? new Date(), 'Pp'),
          },
          {
            customID: '_options',
            columnName: '',
            columnWidth: '50px',
            renderItem: value => html`
              <button
                class="btn btn-ghost btn-sm btn-circle"
                @click=${() => handleRowClick(value)}
                title="View Details"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            `,
          },
        ],
        isSortable: false,
        view: new View({
          filter: new AdornisFilter({
            searchFields: ['odooTicketID', 'subject', 'body', 'answer'],
            search: searchText,
            aspects: filterAspects,
          }),
        }),
      }),
    ];
  }, [environmentFilter, searchText, odooModelsFilter, startDate, endDate, integrationsTest, checkedByHuman]);

  return html`
    <div class="flex gap-4 h-full">
      <div
        class="flex-1 card bg-base-100 shadow-xl border border-base-300 overflow-auto ${selectedConversationID
          ? 'w-1/2'
          : 'w-full'}"
      >
        <div class="card-body">
          <div class="flex justify-between items-center mb-2 sticky top-0 bg-base-100 z-10 py-4">
            <h2 class="card-title text-2xl font-bold text-secondary">Odoo Tickets</h2>

            <div class="flex items-center gap-2">
              <button class="btn btn-sm btn-outline btn-secondary" @click=${() => setShowFilters(!showFilters)}>
                ${showFilters ? 'Hide Filters' : 'Show Filters'}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-4 h-4 ml-1"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
                  />
                </svg>
              </button>
            </div>
          </div>

          ${showFilters
            ? html`
                <div class="bg-base-200 p-4 rounded-lg mb-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <!-- Integrationstest -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Integrationstest</span>
                      </label>
                      <select
                        class="select select-sm select-bordered w-full"
                        .value=${integrationsTest}
                        @change=${(e: InputEvent) =>
                          setIntegrationstest((e.target as HTMLSelectElement).value as 'yes' | 'no')}
                      >
                        <option value="yes">yes</option>
                        <option value="no">no</option>
                      </select>
                    </div>

                    <!-- Checked by Human -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Checked by Human</span>
                      </label>
                      <select
                        class="select select-sm select-bordered w-full"
                        .value=${checkedByHuman}
                        @change=${(e: InputEvent) =>
                          setCheckedByHuman((e.target as HTMLSelectElement).value as 'all' | 'yes' | 'no')}
                      >
                        <option value="all">All</option>
                        <option value="yes">Checked</option>
                        <option value="no">Not Checked</option>
                      </select>
                    </div>

                    <!-- Text Search -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Text Search</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Search in conversations..."
                        class="input input-bordered input-sm w-full"
                        .value=${searchText}
                        @input=${(e: InputEvent) => setSearchText((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- Environment Filter -->
                    <!-- <div class="form-control">
                       <label class="label">
                         <span class="label-text font-medium">Environment</span>
                       </label>
                       <div class="join w-full">
                         <button
                           class="btn btn-sm join-item flex-1 ${environmentFilter === 'all'
                      ? 'btn-primary'
                      : 'btn-outline btn-primary'}"
                           @click=${() => setEnvironmentFilter('all')}
                         >
                           All
                         </button>
                         <button
                           class="btn btn-sm join-item flex-1 ${environmentFilter === ConversationEnvironment.PRODUCTION
                      ? 'btn-primary'
                      : 'btn-outline btn-primary'}"
                           @click=${() => setEnvironmentFilter(ConversationEnvironment.PRODUCTION)}
                         >
                           Production
                         </button>
                         <button
                           class="btn btn-sm join-item flex-1 ${environmentFilter === ConversationEnvironment.STAGING
                      ? 'btn-primary'
                      : 'btn-outline btn-primary'}"
                           @click=${() => setEnvironmentFilter(ConversationEnvironment.STAGING)}
                         >
                           Staging
                         </button>
                       </div>
                     </div> -->

                    <!-- Odoo Models Filter -->
                    <!-- <div class="form-control">
                       <label class="label">
                         <span class="label-text font-medium">Odoo Models</span>
                       </label>
                       <input
                         type="text"
                         placeholder="e.g., res.partner, product.product"
                         class="input input-bordered input-sm w-full"
                         .value=${odooModelsFilter}
                         @input=${(e: InputEvent) => setOdooModelsFilter((e.target as HTMLInputElement).value)}
                       />
                     </div> -->

                    <!-- Date Range - Combined in one container to keep them together -->
                    <div class="form-control col-span-1 md:col-span-2">
                      <label class="label">
                        <span class="label-text font-medium">Date Range</span>
                      </label>
                      <div class="flex gap-2">
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="Start date"
                            class="input input-bordered input-sm w-full"
                            .value=${startDate}
                            @input=${(e: InputEvent) => setStartDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="End date"
                            class="input input-bordered input-sm w-full"
                            .value=${endDate}
                            @input=${(e: InputEvent) => setEndDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Filter Actions -->
                    <div class="col-start-4 flex justify-end mt-4">
                      <button class="btn btn-sm btn-outline btn-error mr-2" @click=${clearFilters}>
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              `
            : nothing}

          <div class="overflow-x-auto">
            ${guard([conversationDataviewOptions], () =>
              DataTable({
                options: conversationDataviewOptions.dataViewOptions,
              }),
            )}
          </div>
        </div>
      </div>

      <!-- Detail view panel -->
      ${selectedConversationID
        ? html`
            <div class="w-1/2">
              ${OdooTicketDetailView({
                conversationID: selectedConversationID,
                onClose: closeDetailView,
              })}
            </div>
          `
        : nothing}
    </div>
  `;
});
